import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { format, parseISO } from 'date-fns'
import { Fragment, useMemo } from 'react'
import { z } from 'zod'
import { CriteriaTooltip } from '~/features/evaluation'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { useLoader } from '~/modules/loader'
import { useTRPC } from '~/services/trpc'
import { RouterOutput } from '~/services/trpc'

import {
  Heading3,
  Icon,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipTrigger,
  cn,
} from '@hap/components'
import { Timespan } from '@hap/utils'
import { toPercent } from '@hap/utils'

import { useAnalyticsPresets } from './hooks'
import { MetricFilters } from './metric-widget'

export function ScorecardSection(props: {
  section: RouterOutput['scorecardSections']['getAll']['data'][0]
  filters: MetricFilters
  timespan: Timespan.Info
  goalFilter: CriteriaScope
  daterange: Timespan.StaticTimespan
}) {
  const trpc = useTRPC()
  const presets = useAnalyticsPresets({
    filters: props.filters,
  })

  // Detect when exactly one scorecard is filtered
  const singleScorecardId = useMemo(() => {
    const scorecardIds = props.filters.scorecardIds?.values
    return scorecardIds?.length === 1 ? scorecardIds[0] : null
  }, [props.filters.scorecardIds])

  // Fetch scorecard-specific data when single scorecard is selected
  const { data: scorecardData } = useQuery(
    trpc.scorecards.getById.queryOptions(singleScorecardId!, {
      enabled: !!singleScorecardId,
    }),
  )

  const { data, loading } = useAnalytics({
    timespan: props.daterange,
    dataset: 'evaluationCriteriaScores',
    filters: presets.hasFilter ? presets.trendFilters : presets.filters,
    aggregations: {
      scorecardCriteriaId: {
        field: 'scorecardCriteriaId',
      },
      avg: {
        field: 'score',
        op: 'avg',
      },
    },
  })

  useLoader(loading)

  const DEFAULT_SERIES = 'week'
  const { data: data2 } = useAnalytics({
    timespan: props.daterange,
    dataset: 'evaluationCriteriaScores',
    filters: presets.hasFilter ? presets.trendFilters : presets.filters,
    aggregations: {
      scorecardCriteriaId: {
        field: 'scorecardCriteriaId',
      },
      avg: {
        field: 'score',
        op: 'avg',
      },
    },
    series: DEFAULT_SERIES,
  })
  type FormattedData = Record<string, Record<string, number>>

  // Helper function to find scorecard-specific assignment for a criteria
  const findCriteriaAssignment = (metricId: string) => {
    if (!scorecardData || !singleScorecardId) return null

    for (const section of scorecardData.sections) {
      const assignment = section.criteria.find((c) => c.criterion.id === metricId)
      if (assignment) return assignment
    }
    return null
  }

  const { formattedData, headersObj } = useMemo<{
    formattedData: FormattedData
    headersObj: Record<string, boolean>
  }>(() => {
    const headersObj: Record<string, boolean> = {}
    const base: FormattedData =
      data?.reduce((acc, d) => {
        return {
          ...acc,
          [d.scorecardCriteriaId]: { avg: d.avg },
        }
      }, {}) || {}

    for (const d of data2 ?? []) {
      const criteria =
        base && base[d.scorecardCriteriaId] ? base[d.scorecardCriteriaId] : {}
      if (d.avg) {
        if (d.time) {
          criteria[d.time] = parseFloat(d.avg)
          headersObj[d.time] = true
        } else {
          criteria['Org Avg'] = parseFloat(d.avg)
          headersObj['Org Avg'] = true
        }
      }

      base[d.scorecardCriteriaId] = criteria
    }
    return { formattedData: base || {}, headersObj }
  }, [data2, data])
  const headers = Object.keys(headersObj)

  return props.section.criteria.length === 0 ? null : (
    <Fragment>
      <TableHeader className="[&_tr]:border-b-0">
        <TableRow>
          <TableCell colSpan={headers.length + 2} className="border-b pt-4">
            <Heading3 className="leading-none">{props.section.name}</Heading3>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableHead>Criteria Name</TableHead>
          {headers.map((header, i) => {
            return (
              <TableHead key={i} className="text-center">
                {DEFAULT_SERIES === 'week' && header !== 'Org Avg'
                  ? format(parseISO(header), 'd MMM yyyy')
                  : header}
              </TableHead>
            )
          })}
          <TableHead className="text-center">Average</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {props.section.criteria
          .filter((metric) => {
            const metricsData = formattedData[metric.id] ?? {}
            const score = data ? metricsData.avg : null
            const belowGoal = score && score < 0.8
            return (
              props.goalFilter === 'All' ||
              (props.goalFilter === 'Above' && !belowGoal) ||
              (props.goalFilter === 'Below' && belowGoal)
            )
          })
          .map((metric, key) => {
            const metricsData = formattedData[metric.id] ?? {}
            const score = data ? metricsData.avg : null
            const belowGoal = score && score < 0.8
            const criteriaAssignment = findCriteriaAssignment(metric.id)

            const avgClassNames = getFillClassNames(metricsData.avg)

            return (
              <TableRow className="border-b-background border-b-2" key={key}>
                <TableCell className="max-w-[300px] truncate text-left font-semibold">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/$alias/indicators/$indicatorId"
                        from="/$alias"
                        params={{ indicatorId: metric.id }}
                        search={{ filters: props.filters, timespan: props.timespan }}
                        className="inline-block truncate"
                      >
                        {belowGoal ? (
                          <Icon
                            name="warning-diamond"
                            className="stroke-warn mr-1 inline"
                          />
                        ) : null}

                        {metric.name}
                      </Link>
                    </TooltipTrigger>
                    <CriteriaTooltip
                      {...metric}
                      assignment={criteriaAssignment}
                      isSingleScorecardFiltered={!!singleScorecardId}
                      autoScoreCriteria={false}
                    />
                  </Tooltip>
                </TableCell>
                {headers.map((weekStart, i) => {
                  const key =
                    typeof weekStart === 'string'
                      ? weekStart
                      : format(weekStart, 'yyyy-MM-dd')
                  const value = metricsData[key]
                  const classNames = getFillClassNames(value)

                  return (
                    <TableCell key={i} align="center" className={classNames}>
                      {toPercent(value)}
                    </TableCell>
                  )
                })}
                <TableCell align="center" className={avgClassNames}>
                  {toPercent(metricsData.avg)}
                </TableCell>
              </TableRow>
            )
          })}
      </TableBody>
    </Fragment>
  )
}

function getFillClassNames(score: number | null) {
  return cn('max-w-[100px] font-semibold border-l-2 border-l-background', {
    'bg-green/40': score !== null && score > 0.8,
    'bg-green/60': score !== null && score > 0.9,
    'bg-transparent': score !== null && score < 0.8,
    'bg-red/20': score !== null && score < 0.45,
  })
}

export const criteriaScope = z.enum(['All', 'Above', 'Below'])
export type CriteriaScope = z.infer<typeof criteriaScope>
